import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, Sparkles, Zap, Crown, Tag, X } from 'lucide-react';
import { fetchPlans, startCheckout, previewPromo, startTrial } from '../api/services';
import { useSubscription, PLAN_NAMES } from '../context/SubscriptionContext';
import { useAuth } from '../context/AuthContext';

const PLAN_ICONS = { free: Sparkles, basic: Zap, live: Crown };

const Subscribe = () => {
  const nav = useNavigate();
  const { user } = useAuth();
  const { sub, refresh, tier: activeTier } = useSubscription();
  const [plans, setPlans] = useState([]);
  const [loadingPlan, setLoadingPlan] = useState(null);
  const [promo, setPromo] = useState('');
  const [promoFor, setPromoFor] = useState('basic');
  const [promoInfo, setPromoInfo] = useState(null);
  const [promoChecking, setPromoChecking] = useState(false);

  useEffect(() => { fetchPlans().then(setPlans).catch(() => {}); }, []);

  const checkPromo = async () => {
    const code = promo.trim().toUpperCase();
    if (!code) { setPromoInfo(null); return; }
    setPromoChecking(true);
    try {
      const res = await previewPromo(code, promoFor, 'monthly');
      setPromoInfo(res);
    } catch {
      setPromoInfo({ valid: false, reason: 'error' });
    } finally { setPromoChecking(false); }
  };

  useEffect(() => {
    if (!promo) { setPromoInfo(null); return; }
    const t = setTimeout(checkPromo, 450);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [promo, promoFor]);

  const choose = async (planId) => {
    if (planId === 'free') { nav('/profiles'); return; }
    setLoadingPlan(planId);
    try {
      const code = promoInfo?.valid && promoFor === planId ? promo.trim().toUpperCase() : null;
      const { url } = await startCheckout(planId, 'monthly', code, '/subscribe/success');
      window.location.href = url;
    } catch (e) {
      alert('Could not start checkout. Please try again.');
      setLoadingPlan(null);
    }
  };

  const beginTrial = async () => {
    if (!user) { nav('/login'); return; }
    setLoadingPlan('basic');
    try {
      await startTrial();
      await refresh();
      nav('/profiles');
    } catch (e) {
      alert(e?.response?.data?.detail || 'Could not start your free trial.');
    } finally { setLoadingPlan(null); }
  };

  const priceFor = (p) => (p.id === 'free' ? { label: '$0', period: '' } : { label: `$${(p.monthly_cents / 100).toFixed(2)}`, period: '/month' });
  const trialAvailable = !sub?.trial_used && !activeTier;

  return (
    <div className="min-h-screen relative overflow-hidden">
      <div className="absolute inset-0">
        <div className="absolute -top-40 -left-40 w-[640px] h-[640px] rounded-full bg-[#E50914]/10 blur-[160px]" />
        <div className="absolute -bottom-32 -right-20 w-[520px] h-[520px] rounded-full bg-amber-500/10 blur-[160px]" />
      </div>

      <div className="relative z-10 px-8 py-10 max-w-7xl mx-auto">
        <div className="flex items-center justify-between">
          <button onClick={() => nav(-1)} className="text-white/70 hover:text-white text-sm">← Back</button>
          {user && <div className="text-white/60 text-sm">Signed in as <span className="text-white">{user.email}</span></div>}
        </div>

        <div className="mt-8 text-center">
          <span className="px-3 py-1 text-[11px] tracking-widest uppercase rounded-full bg-white/8 border border-white/15 text-white/80">Plans • Cancel anytime</span>
          <h1 className="font-display text-5xl md:text-7xl text-white mt-4">Pick your plan</h1>
          <p className="text-white/60 mt-3 max-w-2xl mx-auto">Unlock full HD playback of <span className="text-white font-semibold">BLUNT Originals</span> — exclusive films and series from independent creators. Plus ad-free trailers and discovery for 10,000+ titles.</p>
          {sub?.status === 'active' && (
            <div className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-500/10 border border-emerald-500/40 text-emerald-300 text-sm">
              <Check size={14} /> You're on <span className="font-semibold">{PLAN_NAMES[sub.plan] || sub.plan}{sub.interval === 'trial' ? ' (free trial)' : ''}</span> until {sub.period_end ? new Date(sub.period_end).toLocaleDateString() : ''}
            </div>
          )}
        </div>

        <div className="mt-10 grid md:grid-cols-3 gap-6">
          {plans.map((p) => {
            const Icon = PLAN_ICONS[p.id] || Sparkles;
            const featured = p.id === 'basic';
            const isCurrent = activeTier === p.id && !(p.id === 'basic' && sub?.interval === 'trial');
            const showTrial = p.id === 'basic' && trialAvailable;
            const { label, period } = priceFor(p);
            const discounted = promoInfo?.valid && promoFor === p.id ? promoInfo : null;
            return (
              <div
                key={p.id}
                data-testid={`plan-card-${p.id}`}
                className={`relative p-7 rounded-2xl border ${featured ? 'border-[#E50914]/60 bg-[#E50914]/5' : 'border-white/10 bg-white/5'} backdrop-blur-md`}
              >
                {featured && (
                  <span className="absolute -top-3 left-7 px-3 py-1 rounded-full text-[10px] font-bold tracking-widest uppercase bg-[#E50914] text-white shadow-lg shadow-[#E50914]/40">Most Popular</span>
                )}
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-5 ${featured ? 'bg-[#E50914] text-white' : 'bg-white/10 text-white'}`}>
                  <Icon size={20} />
                </div>
                <h3 className="text-2xl font-semibold text-white">{p.name}</h3>
                <div className="mt-2 flex items-baseline gap-1">
                  {discounted ? (
                    <>
                      <span className="text-4xl font-bold text-white">${(discounted.final_cents/100).toFixed(2)}</span>
                      <span className="text-white/50 text-sm">{period}</span>
                      <span className="ml-2 text-white/40 text-lg line-through">{label}</span>
                    </>
                  ) : (
                    <>
                      <span className="text-4xl font-bold text-white">{label}</span>
                      {period && <span className="text-white/50 text-sm">{period}</span>}
                    </>
                  )}
                </div>
                {p.trial_days && showTrial && !discounted && (
                  <div className="text-emerald-400 text-xs mt-1" data-testid="plan-basic-trial-note">{p.trial_days}-day free trial, then ${(p.monthly_cents / 100).toFixed(2)}/month</div>
                )}
                {discounted && (
                  <div className="text-emerald-400 text-xs mt-1">Promo {promo.trim().toUpperCase()} applied • save ${(discounted.savings_cents/100).toFixed(2)}</div>
                )}
                <ul className="mt-5 space-y-2.5">
                  {p.features.filter((f) => showTrial || !(p.trial_days && f.includes('free trial'))).map((f, i) => (
                    <li key={i} className="flex items-start gap-2 text-white/80 text-[14px]">
                      <Check size={16} className="text-[#E50914] mt-0.5 shrink-0" /> {f}
                    </li>
                  ))}
                </ul>
                <button
                  data-testid={`plan-cta-${p.id}`}
                  onClick={() => { if (showTrial) { beginTrial(); return; } setPromoFor(p.id); choose(p.id); }}
                  disabled={loadingPlan === p.id || isCurrent}
                  className={`w-full mt-7 py-3 rounded-xl font-semibold tv-focusable disabled:opacity-60 ${
                    isCurrent ? 'bg-emerald-500 text-white' :
                    featured ? 'bg-[#E50914] text-white hover:bg-[#f6121d]' :
                    p.id === 'free' ? 'bg-white/10 text-white hover:bg-white/15' :
                    'bg-white text-black hover:bg-white/90'
                  }`}
                >
                  {isCurrent ? 'Current plan' : loadingPlan === p.id ? 'Loading…' : p.id === 'free' ? 'Continue free' : showTrial ? `Start ${p.trial_days}-day free trial` : 'Subscribe with Square'}
                </button>
              </div>
            );
          })}
        </div>

        {/* Promo code input */}
        <div className="mt-8 max-w-md mx-auto">
          <div className="flex items-center gap-2 mb-2">
            <Tag size={14} className="text-white/50" />
            <span className="text-white/70 text-sm">Have a promo code?</span>
            <select value={promoFor} onChange={(e) => setPromoFor(e.target.value)}
              className="ml-auto px-2 py-1 rounded-md bg-white/5 border border-white/10 text-white text-xs">
              <option value="basic">Basic</option>
              <option value="live">Live Events</option>
            </select>
          </div>
          <div className="relative">
            <input
              value={promo}
              onChange={(e) => setPromo(e.target.value.toUpperCase())}
              placeholder="Enter code"
              className="w-full px-4 py-3 pr-11 rounded-xl bg-white/5 border border-white/15 text-white placeholder:text-white/40 focus:outline-none focus:border-white/40 uppercase tracking-wider"
            />
            {promo && (
              <button onClick={() => setPromo('')} className="absolute right-3 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center">
                <X size={14} />
              </button>
            )}
          </div>
          {promoChecking && <div className="text-white/50 text-xs mt-2">Checking…</div>}
          {!promoChecking && promoInfo && (
            promoInfo.valid ? (
              <div className="mt-2 text-emerald-400 text-sm flex items-center gap-1">
                <Check size={14} /> Code valid — save ${(promoInfo.savings_cents/100).toFixed(2)} on {PLAN_NAMES[promoFor]}.
              </div>
            ) : (
              <div className="mt-2 text-white/50 text-sm">Not a valid code for {PLAN_NAMES[promoFor]}.</div>
            )
          )}
        </div>

        <div className="mt-10 text-center text-white/40 text-xs">
          Secure checkout via Square • Production mode.
        </div>
      </div>
    </div>
  );
};

export default Subscribe;
